import { Box, Container, Flex, Text, Heading } from '@radix-ui/themes';
import { useTenantContext } from '@trashlab/sitesapp/src/organizations/junk-rescue/contexts/use-tenant-context';
import { useState, useMemo, useEffect } from 'react';
import { Card } from '@trashlab/sitesapp/src/organizations/junk-rescue/template/components/shared/card';
import {
  IconBook,
  IconRuler,
  IconPricing,
} from '@trashlab/sitesapp/src/organizations/junk-rescue/template/components/icons/icons';
import { useVariantValue } from '@trashlab/sitesapp/src/organizations/junk-rescue/lib/utils/get-variant-value';

type VariantValue = {
  value: string;
  orderIndex?: number;
};

const StepDescription = ({
  description,
  isCarousel = false,
}: {
  description: string[];
  isCarousel?: boolean;
}) => {
  return (
    <Flex direction="column" gap="2">
      {description.map((text, i) => (
        <Text key={i} size="2" className="text-gray-600">
          {text}
        </Text>
      ))}
    </Flex>
  );
};

export const UniqueSkillset = () => {
  const { website } = useTenantContext();
  const [isMobile, setIsMobile] = useState(false);

  // Detect screen size
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint is 1024px
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  const skillSectionSubtitle = useVariantValue(
    'SKILL_SECTION_SUBTITLE',
    'What we specialize in',
  );
  const skillSectionTitle = useVariantValue(
    'SKILL_SECTION_TITLE',
    'Our unique skillset',
  );
  const skillSectionDescription = useVariantValue(
    'SKILL_SECTION_DESCRIPTION',
    `At ${
      website?.organization?.name || 'our company'
    }, we offer comprehensive waste management solutions to suit various needs. Our services are designed to provide convenience, affordability, and reliability for all waste disposal projects.`,
  );

  const defaultSkills = [
    {
      title: `Responsive ${website?.organization?.name || 'company'} service`,
      subtitle: 'A service you can rely on',
      description: [
        'At the heart of our services lies our promise of same-day delivery and pick-up. We understand the urgency of your waste management needs, which is why we ensure your dumpster is delivered promptly, keeping your project timeline intact. Our rapid response and efficiency showcase our commitment to customer satisfaction and service reliability.',
      ],
      icon: IconBook,
    },
    {
      title: 'Broad array of rental options',
      subtitle: 'A vast array of choices to suit your needs',
      description: [
        `${
          website?.organization?.name || 'Our company'
        } brings you an extensive selection of dumpster sizes, tailored to suit various project scales. From residential decluttering tasks to large commercial construction waste management. Our versatility ensures you always have the right dumpster size for your project, ultimately saving time, resources, and facilitating a seamless waste disposal process.`,
      ],
      icon: IconRuler,
    },
    {
      title: 'Affordable and transparent pricing',
      subtitle: 'Fair pricing with no hidden fees',
      description: [
        `We're committed to delivering unsurpassed value with our competitively priced dumpster rental services. At ${
          website?.organization?.name || 'our company'
        }, we believe in affordability paired with quality service. Our transparent pricing structures present no hidden costs, ensuring you can plan your waste management budget confidently and efficiently.`,
      ],
      icon: IconPricing,
    },
  ];

  const iconMap = {
    IconBook,
    IconRuler,
    IconPricing,
  };

  const titles = useVariantValue<VariantValue[]>('SKILL_TITLE', []);
  const subtitles = useVariantValue<VariantValue[]>('SKILL_SUBTITLE', []);
  const contents = useVariantValue<VariantValue[]>('SKILL_CONTENT', []);
  const icons = useVariantValue<VariantValue[]>('SKILL_ICON', []);

  const skills = useMemo(() => {
    if (
      !titles.length &&
      !subtitles.length &&
      !contents.length &&
      !icons.length
    ) {
      return defaultSkills;
    }

    // Get all unique orderIndex values, handling undefined values properly
    const allOrderIndexes = [
      ...titles.map((t) => t.orderIndex),
      ...subtitles.map((s) => s.orderIndex),
      ...contents.map((c) => c.orderIndex),
      ...icons.map((i) => i.orderIndex),
    ].filter((index): index is number => typeof index === 'number');

    const orderIndexes =
      allOrderIndexes.length > 0
        ? [...new Set(allOrderIndexes)].sort((a, b) => a - b)
        : [0, 1, 2]; // Default to first 3 indexes if no orderIndex values

    return orderIndexes
      .map((index) => {
        const title = titles.find((t) => t.orderIndex === index)?.value;
        const subtitle = subtitles.find((s) => s.orderIndex === index)?.value;
        const content = contents.find((c) => c.orderIndex === index)?.value;
        const iconName = icons.find((i) => i.orderIndex === index)?.value;
        const icon = iconName
          ? iconMap[iconName as keyof typeof iconMap]
          : undefined;

        const defaultSkill = defaultSkills[index];
        return {
          title: title ?? defaultSkill?.title ?? '',
          subtitle: subtitle ?? defaultSkill?.subtitle ?? '',
          description: content?.split('\n') ?? defaultSkill?.description ?? [],
          icon: icon ?? defaultSkill?.icon ?? IconBook,
        };
      })
      .filter(
        (skill) =>
          skill.title && skill.subtitle && skill.description.length > 0,
      );
  }, [titles, subtitles, contents, icons, website?.organization?.name]);

  return (
    <Container
      size="4"
      p="8"
      className={`relative min-h-[200px] overflow-hidden before:content-[''] before:absolute before:inset-0 before:bg-[url('/default/rental-steps-bg.svg')] before:bg-cover before:bg-center before:bg-no-repeat before:opacity-100 before:-z-10 after:content-[''] after:absolute after:inset-0 after:opacity-90 after:-z-10`}
    >
      <Flex direction="column" gap="2">
        <Text
          size="2"
          weight="bold"
          className="text-neutral-400"
          data-variable="SKILL_SECTION_SUBTITLE"
        >
          {skillSectionSubtitle}
        </Text>
        <Heading as="h2" size="8" data-variable="SKILL_SECTION_TITLE">
          {skillSectionTitle}
        </Heading>

        <Text
          size="3"
          className="w-[70%]"
          data-variable="SKILL_SECTION_DESCRIPTION"
        >
          {skillSectionDescription}
        </Text>

        {/* Conditional rendering based on screen size to avoid duplicate DOM elements */}
        {!isMobile ? (
          // Desktop version
          <Flex gap="4" className="mt-8">
            {skills.map((skill, index) => (
              <Card key={index} direction="column" gap="2">
                <Flex justify="between" align="center">
                  <Box data-variable="SKILL_ICON" data-order-index={index}>
                    <skill.icon />
                  </Box>
                </Flex>
                <Flex direction="column" gap="2">
                  <Text
                    size="2"
                    className="text-neutral-400"
                    data-variable="SKILL_SUBTITLE"
                    data-order-index={index}
                  >
                    {skill.subtitle}
                  </Text>
                  <Heading
                    size="4"
                    data-variable="SKILL_TITLE"
                    data-order-index={index}
                  >
                    {skill.title}
                  </Heading>
                  <Box data-variable="SKILL_CONTENT" data-order-index={index}>
                    <StepDescription description={skill.description} />
                  </Box>
                </Flex>
              </Card>
            ))}
          </Flex>
        ) : (
          // Mobile version
          <Box className="mt-8 -mx-8">
            <Box
              className="overflow-x-auto px-8 pb-4 hide-scrollbar"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              <Flex gap="4" className="w-max">
                {skills.map((skill, index) => (
                  <Box key={index} className="w-[280px]">
                    <Flex
                      direction="column"
                      gap="2"
                      className="relative p-6 rounded-md shadow-xl bg-white h-full before:absolute before:inset-0 before:shadow-[0_8px_24px_rgba(0,0,0,0.12)] after:absolute after:bottom-0 after:right-0 after:-z-10 after:h-full after:w-full after:translate-x-1 after:translate-y-1 [clip-path:polygon(40px_0,100%_0,100%_calc(100%_-_40px),calc(100%_-_40px)_100%,0_100%,0_40px)] before:[clip-path:polygon(40px_0,100%_0,100%_calc(100%_-_40px),calc(100%_-_40px)_100%,0_100%,0_40px)] after:[clip-path:polygon(40px_0,100%_0,100%_calc(100%_-_40px),calc(100%_-_40px)_100%,0_100%,0_40px)]"
                    >
                      <Flex justify="between" align="center">
                        <Box
                          data-variable="SKILL_ICON"
                          data-order-index={index}
                        >
                          <skill.icon />
                        </Box>
                      </Flex>
                      <Flex direction="column" gap="2">
                        <Text
                          size="2"
                          className="text-neutral-400"
                          data-variable="SKILL_SUBTITLE"
                          data-order-index={index}
                        >
                          {skill.subtitle}
                        </Text>
                        <Heading
                          size="4"
                          data-variable="SKILL_TITLE"
                          data-order-index={index}
                        >
                          {skill.title}
                        </Heading>
                        <Box
                          data-variable="SKILL_CONTENT"
                          data-order-index={index}
                        >
                          <StepDescription
                            description={skill.description}
                            isCarousel
                          />
                        </Box>
                      </Flex>
                    </Flex>
                  </Box>
                ))}
              </Flex>
            </Box>
          </Box>
        )}
      </Flex>
    </Container>
  );
};

export default UniqueSkillset;